import { ChangeEvent, useMemo } from "react";
import {
  Breadcrumbs,
  IconButton,
  Link,
  TablePagination,
  Typography,
} from "@mui/material";
import {
  PageTopBar,
  ThreeSlotLayout,
} from "../../../../../common/components/page_top_bar";
import PrimaryButton from "../../../../../common/components/primary_button";
import DynamicIcon from "../../../../../common/components/dynamic_icon";
import RefreshButton from "../../../../../common/components/refresh_button";
import { StyledDataGrid } from "../../../../../common/components/styled_data_grid";
import { useAuditoriasCadastroViewModel } from "../../viewModels/auditorias_cadastro_view_model";
import { BackButton } from "../../../../../common/components/back_button";
import FormContainer from "../../../../../common/components/form_container";
import { Controller } from "react-hook-form";
import DefaultFormInput from "../../../../../common/components/default_form_input";
import { FormDatePickerInput } from "../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { DropDownNomeAuditor } from "../../../../../common/components/dropdown_nome_auditor";
import BottomBar from "../../../../../common/components/bottom_bar";
import styled from "@emotion/styled";
import { ProcessoAuditoriasLista } from "../../../domain/models/processo_auditorias_lista";
import { GenerateProcessoAuditoriaAuditoriasColumns } from "./columns/auditoria_processos_auditorias";
import DeleteDialog from "../../../../../common/components/delete_dialog";
import { ProcessoModal } from "./components/processo_modal";
import { AuditoriaProcessoAauditoriaForm } from "../../schemas/Auditoria_auditoria/auditoria_processo_auditoria";
import { FormRadioGroupInput } from "../../../../../common/components/masked_inputs/react_forms/form_radio_group_input";
import { ChangeHistoryModal } from "../../../../../common/modals/change_history_modal";
import { isValidId } from "../../../../../common/utils/utils";
import { CopiaProcessoAuditoriaModal } from "./components/copia_auditoria_modal";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export const AuditoriasCadastro = () => {
  const viewModel: any = useAuditoriasCadastroViewModel();
  const AuditoriaProcessoListaColumns = useMemo(() => {
    return GenerateProcessoAuditoriaAuditoriasColumns({
      onActionEditar: viewModel.handleEdicaoProcessoOpenState,
      onActionDeletar: viewModel.handleDeleteProcessoModalOpenState,
      onActionCopiar: viewModel.handleProcessoAuditoriaCopiaOpenState,
    });
  }, [
    viewModel.handleProcessoAuditoriaCopiaOpenState,
    viewModel.handleDeleteProcessoModalOpenState,
    viewModel.handleEdicaoProcessoOpenState,
  ]);
  return (
    <>
      <PageTopBar>
        <ThreeSlotLayout
          rightSlot={<BackButton onClick={viewModel.handleVoltarOpenState} />}
        />
      </PageTopBar>
      <form>
        <div className="flex w-full gap-2 flex-col py-2 px-4 mb-20">
          <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
            <Link underline="hover" color="inherit">
              Auditoria
            </Link>
            <Typography className="text-primary-green">
              Cadastro de Auditoria
            </Typography>
          </Breadcrumbs>
          <div className="div flex justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
            <div className="flex gap-2 items-center w-full">
              <h1 className="text-3xl font-medium text-black dark:text-white">
                Cadastro de Auditoria
              </h1>
              <RefreshButton onClick={viewModel.handleReload} />
            </div>
          </div>
          <div className="w-full bg-light-mode-2 dark:bg-dark-mode-2 p-4 rounded-[4px] border-[1px] border-border-light-mode dark:border-border-dark-mode mb-4">
            <FormContainer title={"Informações Gerais"} arrangement="fullRow">
              <>
                <div className="flex flex-row gap-x-8">
                  <div className="flex items-center justify-start py-2">
                    <FormRadioGroupInput
                      name="TipoAuditoria"
                      label="Tipo Auditoria"
                      readOnly={viewModel.saving}
                      options={[
                        { id: 0, label: "Interna" },
                        { id: 1, label: "Externa" },
                      ]}
                      reactHookForm={viewModel.hookForm}
                    />
                  </div>
                  <div className="flex items-center justify-start py-2">
                    <FormRadioGroupInput
                      name="EmAberto"
                      label="Status"
                      readOnly={viewModel.saving}
                      options={[
                        { id: 0, label: "Encerrado" },
                        { id: 1, label: "Em Aberto" },
                      ]}
                      reactHookForm={viewModel.hookForm}
                    />
                  </div>
                </div>
                <div className="flex flex-row gap-x-8">
                  <DefaultFormInput
                    name="Evento"
                    label="Evento"
                    reactHookForm={viewModel.hookForm}
                  />
                  <FormDatePickerInput
                    name="DataElaboracao"
                    label="Elaborado Em"
                    reactHookForm={viewModel.hookForm}
                  />
                </div>
                <div className="flex flex-row gap-x-8">
                  <FormDatePickerInput
                    name="DataAuditoria"
                    label="Data Início"
                    required={true}
                    reactHookForm={viewModel.hookForm}
                  />

                  <FormDatePickerInput
                    name="DataTermino"
                    label="Data Término"
                    reactHookForm={viewModel.hookForm}
                  />
                </div>
                <DefaultFormInput
                  name="Criterio"
                  label="Escopo da Auditoria"
                  rows={8}
                  multiline={true}
                  reactHookForm={viewModel.hookForm}
                />
              </>
            </FormContainer>
            <FormContainer title={"Auditores"} arrangement="fullRow">
              <>
                <DropDownNomeAuditor
                  name="IdAuditor1"
                  label="Auditor 1"
                  reactHookForm={viewModel.hookForm}
                />
                <DropDownNomeAuditor
                  name="IdAuditor2"
                  label="Auditor 2"
                  reactHookForm={viewModel.hookForm}
                />
                <DropDownNomeAuditor
                  name="IdAuditor3"
                  label="Auditor 3"
                  reactHookForm={viewModel.hookForm}
                />
              </>
            </FormContainer>
            <FormContainer title={"Anexar Arquivos"} arrangement="fullRow">
              <>
                <div className="flex flex-row gap-x-8 w-full">
                  <Controller
                    name="Arquivo1"
                    control={viewModel.hookForm.control}
                    render={({ field }) => (
                      <div className="w-1/2 flex items-center">
                        {viewModel.selectedAuditoria &&
                          viewModel.selectedAuditoria.Arquivo1 &&
                          viewModel.selectedAuditoria.Arquivo1.length > 0 ? (
                          <div className="w-full flex flex-row justify-between items-center">
                            <span
                              className="cursor-pointer flex flex-row items-center gap-2"
                              onMouseDown={() =>
                                viewModel.handleDownload(
                                  viewModel.selectedAuditoria?.nomeArquivo1!,
                                  1,
                                )
                              }
                            >
                              <span className="font-semibold">Arquivo 1:</span>
                              <span>
                                {viewModel.selectedAuditoria?.nomeArquivo1 &&
                                  viewModel.selectedAuditoria.nomeArquivo1
                                    .length > 40
                                  ? `${viewModel.selectedAuditoria.nomeArquivo1.substring(0, 40)}...`
                                  : viewModel.selectedAuditoria?.nomeArquivo1}
                              </span>
                            </span>
                            <IconButton
                              onClick={() =>
                                viewModel.handleArquivoDeleteOpenState(
                                  "limparArquivo1",
                                  "Arquivo1",
                                  true,
                                )
                              }
                            >
                              <DynamicIcon
                                nameIcon="DeleteForeverRounded"
                                size={25}
                                className="text-red-500"
                              />
                            </IconButton>
                          </div>
                        ) : (
                          <PrimaryButton
                            component="label"
                            role={undefined}
                            variant="contained"
                            tabIndex={-1}
                            disabled={viewModel.saving}
                            startIcon={
                              <DynamicIcon
                                nameIcon="Download"
                                size={25}
                                className="text-white"
                              />
                            }
                          >
                            {field.value
                              ? field.value.name
                              : "Escolher arquivo 1"}
                            <VisuallyHiddenInput
                              type="file"
                              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  field.onChange(file);
                                  viewModel.hookForm.setValue(
                                    "nomeArquivo1",
                                    file.name,
                                  );
                                  viewModel.hookForm.setValue("limparArquivo1", false);
                                }
                              }}
                              multiple={false}
                            />
                          </PrimaryButton>
                        )}
                      </div>
                    )}
                  />
                  <Controller
                    name="Arquivo2"
                    control={viewModel.hookForm.control}
                    render={({ field }) => (
                      <div className="w-1/2 flex items-center">
                        {viewModel.selectedAuditoria &&
                          viewModel.selectedAuditoria.Arquivo2 &&
                          viewModel.selectedAuditoria.Arquivo2.length > 0 ? (
                          <div className="w-full flex flex-row justify-between items-center">
                            <span
                              className="cursor-pointer flex flex-row items-center gap-2"
                              onMouseDown={() =>
                                viewModel.handleDownload(
                                  viewModel.selectedAuditoria!.nomeArquivo2!,
                                  2,
                                )
                              }
                            >
                              <span className="font-semibold">Arquivo 2:</span>
                              <span>
                                {viewModel.selectedAuditoria.nomeArquivo2 &&
                                  viewModel.selectedAuditoria.nomeArquivo2
                                    .length > 40
                                  ? `${viewModel.selectedAuditoria.nomeArquivo2.substring(0, 40)}...`
                                  : viewModel.selectedAuditoria.nomeArquivo2}
                              </span>
                            </span>
                            <IconButton
                              onClick={() =>
                                viewModel.handleArquivoDeleteOpenState(
                                  "limparArquivo2",
                                  "Arquivo2",
                                  true,
                                )
                              }
                            >
                              <DynamicIcon
                                nameIcon="DeleteForeverRounded"
                                size={25}
                                className="text-red-500"
                              />
                            </IconButton>
                          </div>
                        ) : (
                          <PrimaryButton
                            component="label"
                            role={undefined}
                            variant="contained"
                            tabIndex={-1}
                            disabled={viewModel.saving}
                            startIcon={
                              <DynamicIcon
                                nameIcon="Download"
                                size={25}
                                className="text-white"
                              />
                            }
                          >
                            {field.value
                              ? field.value.name
                              : "Escolher arquivo 2"}
                            <VisuallyHiddenInput
                              type="file"
                              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  field.onChange(file);
                                  viewModel.hookForm.setValue(
                                    "nomeArquivo2",
                                    file.name,
                                  );
                                }
                                viewModel.hookForm.setValue("limparArquivo2", false); 
                              }}
                              multiple={false}
                            />
                          </PrimaryButton>
                        )}
                      </div>
                    )}
                  />
                </div>
              </>
            </FormContainer>
            <FormContainer title={"Observações"} arrangement="fullRow">
              <>
                <DefaultFormInput
                  name="Observacao"
                  label="Observações"
                  rows={4}
                  multiline={true}
                  reactHookForm={viewModel.hookForm}
                />
              </>
            </FormContainer>
            <span>Programação</span>
            <div className="w-40 mt-4 mb-4">
              <PrimaryButton
                type="button"
                onClick={viewModel.handleProcesso}
                buttonColor={"primary-green"}
              >
                Novo
              </PrimaryButton>
            </div>
            {viewModel.processoAuditoriasLista.length > 0 ? (
              <>
                <StyledDataGrid
                  tableName="auditorias_lista"
                  disableColumnMenu={false}
                  getRowId={(row: ProcessoAuditoriasLista) => row.IdProcesso}
                  columns={AuditoriaProcessoListaColumns}
                  loading={viewModel.loading}
                  rows={viewModel.processoAuditoriasLista}
                  rowCount={viewModel.paginationProcess.totalItems}
                  sx={{
                    "& .status-header": {
                      backgroundColor: "#AFDFD5",
                      color: "#000",
                    },
                  }}
                  pageSizeOptions={[10]}
                  paginationMode="server"
                  paginationModel={{
                    page: viewModel.paginationProcess.page - 1,
                    pageSize: viewModel.paginationProcess.rowsPerPage,
                  }}
                  pinnedColumns={{
                    right: ["IdProcesoAuditoria"],
                  }}
                  autoHeight={true}
                  textSize="medio"
                  getRowHeight={() => "auto"}
                />

                <TablePagination
                  component="div"
                  count={viewModel.paginationProcess.totalItems}
                  page={viewModel.paginationProcess.page - 1}
                  onPageChange={(_, p) => viewModel.handleTableProcessChange(p)}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  rowsPerPage={viewModel.paginationProcess.rowsPerPage}
                  onRowsPerPageChange={(e) =>
                    viewModel.handleRowsPerTableProcessChange(
                      parseInt(e.target.value),
                    )
                  }
                />
              </>
            ) : (
              <></>
            )}
          </div>
        </div>
        <BottomBar>
          <div className="w-full h-full items-center justify-between flex p-4 gap-4">
            <div>
              {isValidId(viewModel.idAuditoria) && (
                <PrimaryButton
                  buttonColor="yellow"
                  onClick={() => viewModel.handleOpenChangeHistory(null, null)}
                >
                  <span className="text-xs">Historico de alterações</span>
                </PrimaryButton>
              )}
            </div>
            <div className="flex flex-row gap-4 ml-auto">
              <div
                className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold"
                onClick={() => viewModel.handleVoltarOpenState()}
              >
                <h1 className="text-sm text-black dark:text-white">Cancelar</h1>
              </div>
              <div className="w-[130px]">
                {viewModel.saving ? (
                  <PrimaryButton>Salvando...</PrimaryButton>
                ) : (
                  <PrimaryButton
                    type="submit"
                    onClick={viewModel.hookForm.handleSubmit(
                      viewModel.handleAuditorialSubmit,
                    )}
                    startIcon={
                      <DynamicIcon
                        nameIcon="CheckRounded"
                        size={25}
                        className="text-white"
                      />
                    }
                  >
                    Salvar
                  </PrimaryButton>
                )}
              </div>
            </div>
          </div>
        </BottomBar>
      </form>

      {/* Modal */}
      <ChangeHistoryModal changeHistoryHandler={viewModel.changeHistory} />
      <ProcessoModal
        open={viewModel.openProcesso}
        IdAuditoria={viewModel.idAuditoria}
        selectedProcessoAuditoria={viewModel.selectProcesso ?? null}
        saving={viewModel.saving}
        selectedProcesso={
          (viewModel.selectProcesso ??
            null) as unknown as AuditoriaProcessoAauditoriaForm | null
        }
        onSubmit={viewModel.handleProcessoAuditorialSubmit}
        onClose={() => viewModel.setOpenProcesso(false)}
        onOpenChangeHistory={(id, tabelaTipo) =>
          viewModel.handleOpenChangeHistory(id, tabelaTipo)
        }
        centroCustoDropdown={viewModel.centroCustoDropdown}
      />
      <CopiaProcessoAuditoriaModal
        open={viewModel.openCopiaModal}
        saving={viewModel.saving}
        selectedProcessoAuditoriaCopia={viewModel.selectedProcessoAuditoria}
        onSubmit={viewModel.handleSubmitCopiaProcessoAuditoria}
        onClose={() =>
          viewModel.handleProcessoAuditoriaCopiaOpenState(null, false)
        }
        IdAuditoria={viewModel.idAuditoria}
        centroCustoDropdown={viewModel.centroCustoDropdown}
      />
      <DeleteDialog
        open={viewModel.openProcessoDelete}
        saving={viewModel.saving}
        title={`Você realmente deseja excluir este item?`}
        onClose={() => viewModel.setOpenProcessoDelete(false)}
        onDelete={viewModel.handleDeleteConfirmaProcessoAuditoria}
      />

      {/* ARQUIVOS */}
      <DeleteDialog
        open={viewModel.openDeleteArquivoModal}
        saving={viewModel.saving}
        onClose={() =>
          viewModel.handleArquivoDeleteOpenState(null, null, false)
        }
        title={`Você realmente deseja excluir este arquivo?`}
        onDelete={viewModel.handleConfirmarDeletarArquivo}
      />
    </>
  );
};
